import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getPublicPlayerApi } from '../api/player.api';
import Card from '../components/common/Card';
import Icon from '../components/common/Icon';
import PitchView from '../components/common/PitchView';
import PlayerHistory from '../components/common/PlayerHistory';
import { toJalaliFull } from '../utils/jalali';

const POSITION_LABELS = {
  GOALKEEPER: 'دروازه‌ بان',
  DEFENDER: 'مدافع',
  MIDFIELDER: 'هافبک',
  FORWARD: 'مهاجم',
};

const AGE_GROUP_LABELS = {
  U10: 'زیر ۱۰ سال',
  U12: 'زیر ۱۲ سال',
  U14: 'زیر ۱۴ سال',
  U16: 'زیر ۱۶ سال',
  U18: 'زیر ۱۸ سال',
  SENIOR: 'بزرگسالان',
};

const GENDER_LABELS = {
  MALE: 'مرد',
  FEMALE: 'زن',
};

const FOOT_LABELS = {
  RIGHT: 'راست',
  LEFT: 'چپ',
  BOTH: 'دو پا',
};

const LEVEL_LABELS = {
  AMATEUR: 'آماتور',
  SEMI_PRO: 'نیمه حرفه‌ای',
  PROFESSIONAL: 'حرفه‌ای',
};

function getImageUrl(path) {
  if (!path) return null;

  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('blob:')
  ) {
    return path;
  }

  const base =
    process.env.REACT_APP_API_BASE_URL?.replace(/\/api\/v1\/?$/, '') ||
    window.location.origin;

  return `${base}${path.startsWith('/') ? '' : '/'}${path}`;
}

function getPositionValue(player) {
  return (
    player?.mainPosition ||
    player?.position ||
    null
  );
}

function getPosition(player) {
  const position = getPositionValue(player);

  return (
    POSITION_LABELS[position] ||
    position ||
    'ثبت نشده'
  );
}

function calculateAge(birthDate) {
  if (!birthDate) return null;

  const birth = new Date(birthDate);

  if (Number.isNaN(birth.getTime())) {
    return null;
  }

  const today = new Date();

  let age =
    today.getFullYear() -
    birth.getFullYear();

  const monthDiff =
    today.getMonth() -
    birth.getMonth();

  if (
    monthDiff < 0 ||
    (
      monthDiff === 0 &&
      today.getDate() < birth.getDate()
    )
  ) {
    age--;
  }

  return age >= 0 ? age : null;
}

function InfoItem({ label, value }) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return null;
  }

  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-4 transition hover:border-emerald-100 hover:bg-emerald-50/40">
      <p className="text-xs font-medium text-slate-400">
        {label}
      </p>

      <p className="mt-1 font-black text-slate-700">
        {value}
      </p>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  className = '',
}) {
  return (
    <Card
      hover
      className={`relative overflow-hidden ${className}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold text-slate-400">
            {label}
          </p>

          <p className="mt-2 text-3xl font-black text-text-dark">
            {value ?? 0}
          </p>
        </div>

        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-xl">
          {icon}
        </div>
      </div>
    </Card>
  );
}

export default function PlayerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setError('');

    getPublicPlayerApi(id)
      .then(({ data }) => {
        if (!mounted) return;

        setPlayer(data?.data || null);
      })
      .catch((err) => {
        if (!mounted) return;

        setPlayer(null);

        setError(
          err?.response?.data?.message ||
          'دریافت اطلاعات بازیکن با خطا مواجه شد.'
        );
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  const age = useMemo(
    () => calculateAge(player?.birthDate),
    [player?.birthDate]
  );

  if (loading) {
    return (
      <section className="container-page animate-fade-in py-20">
        <div className="flex h-72 items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
        </div>
      </section>
    );
  }

  if (!player || error) {
    return (
      <section className="container-page animate-fade-in py-20">
        <Card
          hover={false}
          className="mx-auto max-w-2xl text-center"
        >
          <div className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-red-50 text-red-500">
            <Icon
              name="alert-circle"
              className="h-8 w-8"
            />
          </div>

          <h1 className="text-xl font-black text-text-dark">
            بازیکن پیدا نشد
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            {error ||
              'اطلاعات این بازیکن در دسترس نیست.'}
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-6 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-600"
          >
            بازگشت
          </button>
        </Card>
      </section>
    );
  }

  const fullName =
    `${player.firstName || ''} ${player.lastName || ''}`.trim();

  const positionValue = getPositionValue(player);
  const positionLabel = getPosition(player);

  const pitchPlayer = {
    ...player,
    photo: getImageUrl(player.photo),
    mainPosition: positionValue,
    position: positionValue,
  };

  return (
    <section className="container-page animate-fade-in py-10 sm:py-14 lg:py-16">

      {/* Back */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-6 inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-slate-500 transition hover:bg-emerald-50 hover:text-emerald-600"
      >
        <Icon
          name="arrow-right"
          className="h-4 w-4"
        />

        بازگشت به بازیکنان
      </button>

      {/* =========================================================
          Hero بازیکن
          ========================================================= */}
      <Card
        hover={false}
        className="overflow-hidden p-0"
      >
        <div className="relative overflow-hidden bg-gradient-to-br from-[#07150F] via-[#0E3A27] to-emerald-700 p-6 text-white sm:p-8 lg:p-10">

          <div className="pitch-stripes pointer-events-none absolute inset-0" />

          <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-floodlight/15 blur-3xl" />

          <div className="pointer-events-none absolute -bottom-32 right-0 h-80 w-80 rounded-full bg-floodlight/15 blur-3xl" />

          <div className="relative flex flex-col items-center gap-7 md:flex-row">

            {/* Photo */}
            <div className="relative shrink-0">

              {player.photo ? (
                <img
                  src={getImageUrl(player.photo)}
                  alt={fullName}
                  className="h-32 w-32 rounded-[2rem] border-4 border-white/30 object-cover shadow-2xl sm:h-36 sm:w-36"
                />
              ) : (
                <div className="grid h-32 w-32 place-items-center rounded-[2rem] border-4 border-white/30 bg-white/20 text-5xl font-black shadow-2xl sm:h-36 sm:w-36">
                  {player.firstName?.charAt(0) || '⚽'}
                </div>
              )}

              {player.jerseyNumber && (
                <div className="absolute -bottom-3 -right-3 grid h-12 w-12 place-items-center rounded-full bg-white text-lg font-black text-emerald-600 shadow-lg">
                  {player.jerseyNumber}
                </div>
              )}
            </div>

            {/* Main info */}
            <div className="min-w-0 flex-1 text-center md:text-right">

              <p className="text-sm font-bold text-emerald-100">
                پروفایل عمومی بازیکن
              </p>

              <h1 className="mt-2 text-3xl font-black sm:text-4xl">
                {fullName}
              </h1>

              <div className="mt-3 flex flex-wrap items-center justify-center gap-2 md:justify-start">

                <span className="rounded-full border border-white/15 bg-white/15 px-4 py-2 text-sm font-black backdrop-blur">
                  {positionLabel}
                </span>

                {player.secondPosition && (
                  <span className="rounded-full border border-white/10 bg-black/10 px-4 py-2 text-xs font-bold text-white/80">
                    پست دوم: {POSITION_LABELS[player.secondPosition] || player.secondPosition}
                  </span>
                )}

                {player.playerLevel && (
                  <span className="rounded-full border border-white/10 bg-black/10 px-4 py-2 text-xs font-bold text-white/80">
                    {LEVEL_LABELS[player.playerLevel] || player.playerLevel}
                  </span>
                )}

              </div>

              {player.club && (
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/clubs/${player.club.id}`)
                  }
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-bold backdrop-blur transition hover:bg-white/25"
                >
                  {player.club.logo && (
                    <img
                      src={getImageUrl(player.club.logo)}
                      alt=""
                      className="h-6 w-6 rounded-full object-cover"
                    />
                  )}

                  {player.club.name}

                  <Icon
                    name="arrow-left"
                    className="h-4 w-4"
                  />
                </button>
              )}

            </div>
          </div>
        </div>

        {/* Quick info */}
        <div className="grid grid-cols-2 gap-3 p-5 sm:p-6 md:grid-cols-4">

          <InfoItem
            label="گروه سنی"
            value={
              AGE_GROUP_LABELS[player.ageGroup] ||
              player.ageGroup
            }
          />

          <InfoItem
            label="جنسیت"
            value={
              GENDER_LABELS[player.gender] ||
              player.gender
            }
          />

          <InfoItem
            label="سن"
            value={
              age !== null
                ? `${age} سال`
                : null
            }
          />

          <InfoItem
            label="سطح"
            value={
              LEVEL_LABELS[player.playerLevel] ||
              player.playerLevel
            }
          />

        </div>
      </Card>

      {/* =========================================================
          موقعیت بازیکن در زمین
          ========================================================= */}
      <section className="mt-8">

        <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">

          <div>

            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-black text-emerald-600">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />

              موقعیت تاکتیکی
            </div>

            <h2 className="mt-3 text-2xl font-black text-text-dark">
              جایگاه {fullName} در زمین
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              پست اصلی بازیکن روی زمین فوتبال مشخص شده است.
            </p>

          </div>

          <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-500 shadow-sm">
            {positionLabel}
          </div>

        </div>

        <PitchView players={[pitchPlayer]} />

      </section>

      {/* =========================================================
          Statistics
          ========================================================= */}
      <section className="mt-10">

        <div className="mb-5 flex items-end justify-between">
          <div>
            <span className="text-xs font-black text-emerald-600">
              عملکرد بازیکن
            </span>

            <h2 className="mt-1 text-2xl font-black text-text-dark">
              آمار عملکرد
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">

          <StatCard
            label="گل"
            value={player.goals}
            icon="⚽"
          />

          <StatCard
            label="پاس گل"
            value={player.assists}
            icon="🎯"
          />

          <StatCard
            label="کارت زرد"
            value={player.yellowCards}
            icon="🟨"
          />

          <StatCard
            label="کارت قرمز"
            value={player.redCards}
            icon="🟥"
          />

        </div>
      </section>

      {/* =========================================================
          Personal information
          ========================================================= */}
      <section className="mt-10">

        <h2 className="mb-5 text-2xl font-black text-text-dark">
          اطلاعات بازیکن
        </h2>

        <Card hover={false}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">

            <InfoItem
              label="تاریخ تولد"
              value={
                player.birthDate
                  ? toJalaliFull(player.birthDate)
                  : null
              }
            />

            <InfoItem
              label="محل تولد"
              value={player.birthPlace}
            />

            <InfoItem
              label="شهر"
              value={player.city}
            />

            <InfoItem
              label="ملیت"
              value={player.nationality}
            />

            <InfoItem
              label="قد"
              value={
                player.height
                  ? `${player.height} سانتی‌ متر`
                  : null
              }
            />

            <InfoItem
              label="وزن"
              value={
                player.weight
                  ? `${player.weight} کیلوگرم`
                  : null
              }
            />

            <InfoItem
              label="پای ترجیحی"
              value={
                FOOT_LABELS[player.preferredFoot] ||
                player.preferredFoot
              }
            />

            <InfoItem
              label="پست اصلی"
              value={positionLabel}
            />

            <InfoItem
              label="پست دوم"
              value={
                POSITION_LABELS[player.secondPosition] ||
                player.secondPosition
              }
            />

            <InfoItem
              label="شماره پیراهن"
              value={
                player.jerseyNumber
                  ? `#${player.jerseyNumber}`
                  : null
              }
            />

            <InfoItem
              label="تاریخ ثبت"
              value={
                player.registrationDate
                  ? toJalaliFull(player.registrationDate)
                  : null
              }
            />

          </div>
        </Card>
      </section>

      {/* =========================================================
          Biography
          ========================================================= */}
      {player.bio && (
        <section className="mt-10">

          <h2 className="mb-5 text-2xl font-black text-text-dark">
            درباره بازیکن
          </h2>

          <Card hover={false}>
            <p className="whitespace-pre-line text-sm leading-8 text-slate-600">
              {player.bio}
            </p>
          </Card>

        </section>
      )}

      {/* =========================================================
          Previous clubs
          ========================================================= */}
      {player.previousClubs && (
        <section className="mt-10">

          <h2 className="mb-5 text-2xl font-black text-text-dark">
            سابقه باشگاهی
          </h2>

          <Card hover={false}>
            <p className="whitespace-pre-line text-sm leading-8 text-slate-600">
              {player.previousClubs}
            </p>
          </Card>

        </section>
      )}

      {/* =========================================================
          Gallery
          ========================================================= */}
      {player.images?.length > 0 && (
        <section className="mt-10">

          <h2 className="mb-5 text-2xl font-black text-text-dark">
            تصاویر
          </h2>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">

            {player.images.map((image) => (
              <Card
                key={image.id}
                hover
                className="overflow-hidden p-0"
              >
                <img
                  src={getImageUrl(image.url)}
                  alt={
                    image.caption ||
                    fullName
                  }
                  className="h-56 w-full object-cover"
                />

                {image.caption && (
                  <p className="p-3 text-sm text-slate-500">
                    {image.caption}
                  </p>
                )}

              </Card>
            ))}

          </div>
        </section>
      )}

      {/* =========================================================
          Social
          ========================================================= */}
      {(player.instagramUrl ||
        player.telegramUrl) && (
        <section className="mt-10">

          <h2 className="mb-5 text-2xl font-black text-text-dark">
            شبکه‌های اجتماعی
          </h2>

          <div className="flex flex-wrap gap-3">

            {player.instagramUrl && (
              <a
                href={player.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-pink-50 px-5 py-3 text-sm font-bold text-pink-600 transition hover:bg-pink-100"
              >
                <Icon
                  name="instagram"
                  className="h-5 w-5"
                />

                اینستاگرام
              </a>
            )}

            {player.telegramUrl && (
              <a
                href={player.telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-blue-50 px-5 py-3 text-sm font-bold text-blue-600 transition hover:bg-blue-100"
              >
                <Icon
                  name="telegram"
                  className="h-5 w-5"
                />

                تلگرام
              </a>
            )}

          </div>
        </section>
      )}

      {/* =========================================================
          Back to club
          ========================================================= */}
      {player.club && (
        <section className="mt-10">

          <Card
            hover
            onClick={() =>
              navigate(`/clubs/${player.club.id}`)
            }
            className="cursor-pointer border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white"
          >
            <div className="flex items-center justify-between gap-4">

              <div className="flex min-w-0 items-center gap-4">

                {player.club.logo ? (
                  <img
                    src={getImageUrl(player.club.logo)}
                    alt={player.club.name}
                    className="h-14 w-14 shrink-0 rounded-2xl object-cover"
                  />
                ) : (
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-emerald-100 text-xl font-black text-emerald-600">
                    ⚽
                  </div>
                )}

                <div className="min-w-0">
                  <p className="text-xs text-slate-400">
                    باشگاه فعلی
                  </p>

                  <h3 className="mt-1 truncate font-black text-text-dark">
                    {player.club.name}
                  </h3>

                  <p className="mt-1 text-sm text-slate-500">
                    مشاهده پروفایل باشگاه
                  </p>
                </div>

              </div>

              <Icon
                name="arrow-left"
                className="h-5 w-5 shrink-0 text-emerald-600"
              />

            </div>
          </Card>

        </section>
      )}

      <PlayerHistory player={player} />

    </section>
  );
}